import { useState } from 'react';
import type { Priority, StatusCategory, WorkItemType } from '../lib/types';
import { WORK_ITEM_TYPE_LABELS } from '../lib/types';
import type { BoardFilter } from '../lib/boardFilter';
import { EMPTY_FILTER, activeCount, isFiltering } from '../lib/boardFilter';

const PRIORITIES: Priority[] = ['critical', 'high', 'medium', 'low'];
const STATUS_CATEGORIES: StatusCategory[] = ['backlog', 'todo', 'active', 'blocked', 'review', 'done'];

/**
 * What there is to filter by here. Handed in rather than derived, because a board and a
 * cross-company list know different things: one has sections, the other has projects, and
 * neither should offer a choice that cannot match anything.
 */
export interface FilterFacets {
  people: Array<{ id: string; name: string }>;
  categories: Array<{ id: string; path: string }>;
  /** Sections, on a board. */
  groups?: Array<{ id: string; name: string }>;
  /** Projects, on a list that spans them. */
  projects?: Array<{ id: string; name: string; code: string }>;
}

/**
 * Narrowing the board, once, for all three views.
 *
 * The extra conditions open in normal flow rather than in a popover. The board is a scroll
 * container and a menu inside one gets clipped — the trap the notification panel fell into —
 * and a filter panel is big enough that pushing the board down is honest anyway.
 */
export function WorkFilters({
  facets,
  filter,
  onChange,
  shown,
  total,
  meId,
  placeholder,
  lockedType,
}: {
  /** Derived from the UNFILTERED list, so the choices do not disappear as you use them. */
  facets: FilterFacets;
  filter: BoardFilter;
  onChange: (next: BoardFilter) => void;
  shown: number;
  total: number;
  /** Set where the page is fixed to one type, which hides the Type chips entirely. */
  lockedType?: boolean;
  meId: string | null;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const count = activeCount(filter);
  const filtering = isFiltering(filter);

  const set = <K extends keyof BoardFilter>(key: K, value: BoardFilter[K]) => onChange({ ...filter, [key]: value });

  const toggleIn = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  return (
    <div className="filter-bar mb-2">
      <div className="row wrap" style={{ gap: 8 }}>
        <input
          type="search"
          className="filter-search"
          value={filter.text}
          placeholder={placeholder ?? 'Find — key, title, wording'}
          onChange={(event) => set('text', event.target.value)}
        />

        {meId && (
          <button
            type="button"
            className={filter.assignee === 'me' ? 'chip-toggle is-on' : 'chip-toggle'}
            aria-pressed={filter.assignee === 'me'}
            onClick={() => set('assignee', filter.assignee === 'me' ? 'any' : 'me')}
          >
            Mine
          </button>
        )}
        <button
          type="button"
          className={filter.overdue ? 'chip-toggle is-on' : 'chip-toggle'}
          aria-pressed={filter.overdue}
          onClick={() => set('overdue', !filter.overdue)}
        >
          Overdue
        </button>
        <button
          type="button"
          className={filter.blocked ? 'chip-toggle is-on' : 'chip-toggle'}
          aria-pressed={filter.blocked}
          onClick={() => set('blocked', !filter.blocked)}
        >
          Blocked
        </button>
        <button
          type="button"
          className={filter.atRisk ? 'chip-toggle is-on' : 'chip-toggle'}
          aria-pressed={filter.atRisk}
          title="Against its SLA: at risk or already breached"
          onClick={() => set('atRisk', !filter.atRisk)}
        >
          SLA at risk
        </button>

        <button
          type="button"
          className={open ? 'chip-toggle is-on' : 'chip-toggle'}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          More filters{count > 0 ? ` · ${count}` : ''}
        </button>

        <span className="grow" />

        <span className="dim" style={{ fontSize: 12 }}>
          {filtering ? (
            <>
              <strong>{shown}</strong> of {total} shown
            </>
          ) : (
            `${total} task${total === 1 ? '' : 's'}`
          )}
        </span>
        {filtering && (
          <button type="button" className="btn btn-quiet btn-sm" onClick={() => onChange({ ...EMPTY_FILTER })}>
            Clear
          </button>
        )}
      </div>

      {open && (
        <div className="filter-more">
          <div className="filter-group">
            <span className="filter-label">Priority</span>
            <span className="row wrap" style={{ gap: 5 }}>
              {PRIORITIES.map((priority) => (
                <button
                  key={priority}
                  type="button"
                  className={filter.priorities.includes(priority) ? 'chip-toggle is-on' : 'chip-toggle'}
                  aria-pressed={filter.priorities.includes(priority)}
                  onClick={() => set('priorities', toggleIn(filter.priorities, priority))}
                >
                  {priority}
                </button>
              ))}
            </span>
          </div>

          {/*
            Hidden where the page is locked to a type — the Bugs list is every bug and nothing
            else, so offering chips that the lock silently overrides is a control that cannot
            win. The same rule the document button followed.
          */}
          {!lockedType && (
          <div className="filter-group">
            <span className="filter-label">Type</span>
            <span className="row wrap" style={{ gap: 5 }}>
              {(Object.keys(WORK_ITEM_TYPE_LABELS) as WorkItemType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  className={filter.types.includes(type) ? 'chip-toggle is-on' : 'chip-toggle'}
                  aria-pressed={filter.types.includes(type)}
                  onClick={() => set('types', toggleIn(filter.types, type))}
                >
                  {WORK_ITEM_TYPE_LABELS[type]}
                </button>
              ))}
            </span>
          </div>
          )}

          <div className="filter-group">
            <span className="filter-label">Assigned to</span>
            {/* A native select: no popover to clip, and it works from the keyboard as it is. */}
            <select value={filter.assignee} onChange={(event) => set('assignee', event.target.value)}>
              <option value="any">Anybody</option>
              {meId && <option value="me">Me</option>}
              <option value="none">Nobody yet</option>
              {facets.people.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.name}
                </option>
              ))}
            </select>
          </div>

          {(facets.groups?.length ?? 0) > 0 && (
            <div className="filter-group">
              <span className="filter-label">Section</span>
              <select value={filter.group} onChange={(event) => set('group', event.target.value)}>
                <option value="any">Any section</option>
                {facets.groups!.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
                <option value="none">Not in a section</option>
              </select>
            </div>
          )}

          {facets.categories.length > 0 && (
            <div className="filter-group">
              <span className="filter-label">About</span>
              <select value={filter.category} onChange={(event) => set('category', event.target.value)}>
                <option value="any">Anything</option>
                {facets.categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.path}
                  </option>
                ))}
                <option value="none">Not categorised</option>
              </select>
            </div>
          )}

          {(facets.projects?.length ?? 0) > 1 && (
            <div className="filter-group">
              <span className="filter-label">Project</span>
              <select value={filter.project} onChange={(event) => set('project', event.target.value)}>
                <option value="any">Every project</option>
                {facets.projects!.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(facets.projects?.length ?? 0) > 1 && (
            <div className="filter-group">
              <span className="filter-label">Stage</span>
              {/* By category, not by column: status ids differ per project. */}
              <select
                value={filter.statusCategory}
                onChange={(event) => set('statusCategory', event.target.value as StatusCategory | 'any')}
              >
                <option value="any">Any stage</option>
                {STATUS_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="filter-group">
            <span className="filter-label">Also</span>
            <span className="row wrap" style={{ gap: 5 }}>
              <button
                type="button"
                className={filter.hasMerge ? 'chip-toggle is-on' : 'chip-toggle'}
                aria-pressed={filter.hasMerge}
                onClick={() => set('hasMerge', !filter.hasMerge)}
              >
                Has a merge request
              </button>
              <button
                type="button"
                className={filter.unestimated ? 'chip-toggle is-on' : 'chip-toggle'}
                aria-pressed={filter.unestimated}
                title="No points and no estimate — the work nobody has sized"
                onClick={() => set('unestimated', !filter.unestimated)}
              >
                Not sized
              </button>
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
